"use client";

import { useForm } from '@tanstack/react-form'
import { Input } from '@/src/components/ui/input'
import { Label } from '@/src/components/ui/label'
import { Textarea } from '@/src/components/ui/textarea'
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '@/src/components/ui/select'
import { Checkbox } from '@/src/components/ui/checkbox'
import { Button } from '@/src/components/ui/button'
import { ShortName } from '@/src/components/common/ShortName'
import { CaretDownIcon, CaretUpIcon, FloppyDiskIcon } from '@phosphor-icons/react'
import { useEffect, useState } from 'react'
import type { FieldDefinition, FormSchemaDefinition } from '@/shared/schemas/operation-data.zod'
import { resolveLocalizedString } from '@/src/lib/i18n-utils'

interface DynamicFormProps {
  schema: FormSchemaDefinition
  defaultValues?: Record<string, unknown>
  onSubmit?: (values: Record<string, unknown>) => void | Promise<void>
  onCancel?: () => void
  submitLabel?: string
  cancelLabel?: string
  isPending?: boolean
  locale?: string // Current locale for resolving localized strings
  showModeToggle?: boolean
  initialMode?: 'basic' | 'expert'
  mode?: 'basic' | 'expert'
  onModeChange?: (mode: 'basic' | 'expert') => void
  showActions?: boolean
  readOnly?: boolean
}

/**
 * DynamicForm - Generates forms dynamically from a FormSchemaDefinition.
 * Uses TanStack Form for form management.
 * Supports localized labels and descriptions via the locale prop.
 */
export function DynamicForm({
  schema,
  defaultValues = {},
  onSubmit,
  onCancel,
  submitLabel = 'Save',
  cancelLabel = 'Cancel',
  isPending = false,
  locale = 'en',
  showModeToggle = true,
  initialMode = 'basic',
  mode: controlledMode,
  onModeChange,
  showActions = true,
  readOnly = false,
}: DynamicFormProps) {
  const [internalMode, setInternalMode] = useState<'basic' | 'expert'>(initialMode)
  const mode = controlledMode ?? internalMode
  const setMode = onModeChange ?? setInternalMode
  const hasExpertFields = schema.fields.some((field) => field.visibility === 'expert')

  // Track collapsed groups
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(() => {
    const collapsed = new Set<string>()
    schema.groups?.forEach(group => {
      if (!group.defaultOpen) {
        collapsed.add(group.name)
      }
    })
    return collapsed
  })

  useEffect(() => {
    const collapsed = new Set<string>()
    schema.groups?.forEach((group) => {
      if (!group.defaultOpen) {
        collapsed.add(group.name)
      }
    })
    setCollapsedGroups(collapsed)
  }, [schema.groups])
  
  const form = useForm({
    defaultValues: {
      ...getDefaultValuesFromSchema(schema.fields),
      ...defaultValues,
    },
    onSubmit: async ({ value }) => {
      if (onSubmit) {
        await onSubmit(value)
      }
    },
  })
  
  const toggleGroup = (groupName: string) => {
    setCollapsedGroups(prev => {
      const next = new Set(prev)
      if (next.has(groupName)) {
        next.delete(groupName)
      } else {
        next.add(groupName)
      }
      return next
    })
  }
  
  // Group fields by their group
  const visibleFields = schema.fields.filter((field) => mode === 'expert' || field.visibility !== 'expert')
  const groupedFields = groupFields(visibleFields, schema.groups, locale)
  
  // Determine grid layout based on schema.layout
  const layoutClass = schema.layout === 'single-col' 
    ? 'grid-cols-1' 
    : 'grid-cols-1 md:grid-cols-2'
  
  return (
    <form
      onSubmit={(e) => {
        e.preventDefault()
        form.handleSubmit()
      }}
      className="space-y-4"
    >
      {showModeToggle && hasExpertFields && (
        <div className="flex items-center justify-end gap-2">
          <Button
            type="button"
            size="sm"
            variant={mode === 'basic' ? 'default' : 'outline'}
            onClick={() => setMode('basic')}
          >
            Basic
          </Button>
          <Button
            type="button"
            size="sm"
            variant={mode === 'expert' ? 'default' : 'outline'}
            onClick={() => setMode('expert')}
          >
            Expert
          </Button>
        </div>
      )}

      {/* Fields without groups */}
      {groupedFields.ungrouped.length > 0 && (
        <div className={`grid ${layoutClass} gap-4`}>
          {groupedFields.ungrouped.map(field => (
            <DynamicField key={field.name} field={field} form={form} locale={locale} readOnly={readOnly} />
          ))}
        </div>
      )}
      
      {/* Grouped fields */}
      {groupedFields.groups.map(group => (
        <div key={group.name} className="pb-4">
          {group.collapsible ? (
            <button
              type="button"
              className="flex items-center justify-between w-full text-left mb-4"
              onClick={() => toggleGroup(group.name)}
            >
              <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
                {group.label}
              </h3>
              {collapsedGroups.has(group.name) ? (
                <CaretDownIcon className="w-4 h-4 text-muted-foreground" />
              ) : (
                <CaretUpIcon className="w-4 h-4 text-muted-foreground" />
              )}
            </button>
          ) : (
            <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide mb-4">
              {group.label}
            </h3>
          )}
          
          {(!group.collapsible || !collapsedGroups.has(group.name)) && (
            <div className={`grid ${getGroupLayoutClass(group.sectionColumns, layoutClass)} gap-4`}>
              {group.fields.map(field => (
                <DynamicField key={field.name} field={field} form={form} locale={locale} readOnly={readOnly} />
              ))}
            </div>
          )}
        </div>
      ))}
      
      {/* Submit buttons */}
      {showActions && !readOnly && (
        <div className="flex justify-end gap-2">
          {onCancel && (
            <Button type="button" variant="outline" onClick={onCancel}>
              {cancelLabel}
            </Button>
          )}
          <Button type="submit" disabled={isPending}>
            <FloppyDiskIcon className="w-4 h-4 mr-2" />
            {submitLabel}
          </Button>
        </div>
      )}
    </form>
  )
}

/**
 * DynamicField - Renders a single form field based on its definition.
 * Uses 'any' types for TanStack Form due to complex generic requirements.
 * Supports localized labels and descriptions.
 */
 
function DynamicField({ field, form, locale = 'en', readOnly = false }: { field: FieldDefinition; form: any; locale?: string; readOnly?: boolean }) {
  const resolvedLabel = resolveLocalizedString(field.label, locale)
  const label = resolvedLabel || field.shortName || field.name
  const description = field.description ? resolveLocalizedString(field.description, locale) : undefined
  const shortNamePrefix = (
    <span className="text-xs text-muted-foreground font-mono whitespace-nowrap pt-2 inline-flex min-w-12 justify-end text-right">
      {field.shortName ? <ShortName>{field.shortName}</ShortName> : <span className="invisible">xxxx</span>}
    </span>
  )
  
  return (
    <form.Field
      name={field.name}
       
      children={(fieldApi: any) => (
        <div className={field.type === 'textarea' ? 'md:col-span-2' : ''}>
          <Label htmlFor={field.name}>
            {label}
            {field.required && ' *'}
          </Label>
          
          {field.type === 'text' && (
            <div className="flex items-center gap-2">
              {shortNamePrefix}
              <Input
                id={field.name}
                value={(fieldApi.state.value as string) ?? ''}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => fieldApi.handleChange(e.target.value)}
                onBlur={fieldApi.handleBlur}
                placeholder={field.placeholder}
                readOnly={readOnly}
                disabled={readOnly}
              />
            </div>
          )}
          
          {field.type === 'number' && (
            <div className="flex items-center gap-2">
              {shortNamePrefix}
              <Input
                id={field.name}
                type="number"
                min={field.min}
                max={field.max}
                step={field.step}
                value={(fieldApi.state.value as number | undefined) ?? ''}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => fieldApi.handleChange(e.target.value ? Number(e.target.value) : undefined)}
                onBlur={fieldApi.handleBlur}
                placeholder={field.placeholder}
                readOnly={readOnly}
                disabled={readOnly}
              />
              <span className="text-sm text-muted-foreground whitespace-nowrap inline-flex min-w-10">
                {field.unit ? field.unit : <span className="invisible">unit</span>}
              </span>
            </div>
          )}
          
          {field.type === 'textarea' && (
            <div className="flex items-start gap-2">
              {shortNamePrefix}
              <Textarea
                id={field.name}
                value={(fieldApi.state.value as string) ?? ''}
                onChange={(e) => fieldApi.handleChange(e.target.value)}
                onBlur={fieldApi.handleBlur}
                placeholder={field.placeholder}
                rows={3}
                readOnly={readOnly}
                disabled={readOnly}
              />
            </div>
          )}
          
          {field.type === 'select' && field.options && (
            <div className="flex items-center gap-2">
              {shortNamePrefix}
              <Select
                value={(fieldApi.state.value as string) ?? ''}
                onValueChange={(v) => v && fieldApi.handleChange(v)}
                disabled={readOnly}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder={field.placeholder || 'Select...'} />
                </SelectTrigger>
                <SelectContent>
                  {field.options.map(option => (
                    <SelectItem key={option.value} value={option.value}>
                      {resolveLocalizedString(option.label, locale)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          
          {field.type === 'date' && (
            <div className="flex items-center gap-2">
              {shortNamePrefix}
              <Input
                id={field.name}
                type="date"
                value={(fieldApi.state.value as string) ?? ''}
                onChange={(e) => fieldApi.handleChange(e.target.value)}
                onBlur={fieldApi.handleBlur}
                readOnly={readOnly}
                disabled={readOnly}
              />
            </div>
          )}
          
          {field.type === 'checkbox' && (
            <div className="flex items-center gap-2">
              <Checkbox
                id={field.name}
                checked={(fieldApi.state.value as boolean) ?? false}
                onCheckedChange={(checked) => fieldApi.handleChange(!!checked)}
                disabled={readOnly}
              />
              {description && (
                <span className="text-sm text-muted-foreground">{description}</span>
              )}
            </div>
          )}
          
          {description && field.type !== 'checkbox' && (
            <p className="text-xs text-muted-foreground mt-1">{description}</p>
          )}
        </div>
      )}
    />
  )
}

/**
 * Get default values from field definitions.
 * Supports the new defaultValue property on fields.
 */
function getDefaultValuesFromSchema(fields: FieldDefinition[]): Record<string, any> {
  const defaults: Record<string, any> = {}
  
  for (const field of fields) {
    // If field has explicit defaultValue, use it
    if (field.defaultValue !== undefined) {
      defaults[field.name] = field.defaultValue
      continue
    }
    
    // Otherwise, use type-based defaults
    switch (field.type) {
      case 'checkbox':
        defaults[field.name] = false
        break
      case 'number':
        defaults[field.name] = undefined
        break
      default:
        defaults[field.name] = ''
    }
  }
  
  return defaults
}

/**
 * Group fields by their group property.
 * Supports localized group labels.
 */
function groupFields(
  fields: FieldDefinition[],
  groups?: FormSchemaDefinition['groups'],
  locale: string = 'en'
): {
  ungrouped: FieldDefinition[]
  groups: Array<{
    name: string
    label: string
    collapsible: boolean
    sectionColumns: 1 | 2 | 3 | 4
    fields: FieldDefinition[]
  }>
} {
  const ungrouped: FieldDefinition[] = []
  const groupedMap: Map<string, FieldDefinition[]> = new Map()
  
  for (const field of fields) {
    if (field.group) {
      if (!groupedMap.has(field.group)) {
        groupedMap.set(field.group, [])
      }
      groupedMap.get(field.group)!.push(field)
    } else {
      ungrouped.push(field)
    }
  }
  
  const groupedResult = (groups || [])
    .filter(g => groupedMap.has(g.name))
    .map(g => ({
      name: g.name,
      label: resolveLocalizedString(g.label, locale),
      collapsible: g.collapsible ?? true,
      sectionColumns: (g.sectionColumns ?? 1) as 1 | 2 | 3 | 4,
      fields: groupedMap.get(g.name) || [],
    }))
  
  return { ungrouped, groups: groupedResult }
}

function getGroupLayoutClass(sectionColumns: 1 | 2 | 3 | 4, fallback: string): string {
  if (sectionColumns <= 1) return 'grid-cols-1'
  if (sectionColumns === 2) return 'grid-cols-1 md:grid-cols-2'
  if (sectionColumns === 3) return 'grid-cols-1 md:grid-cols-2 xl:grid-cols-3'
  if (sectionColumns === 4) return 'grid-cols-1 md:grid-cols-2 xl:grid-cols-4'
  return fallback
}
